import * as React from 'react';
import {styled, Theme, CSSObject} from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import {useEffect, useState} from 'react';
import LocalDB from 'src/utils/localDB';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {
  selectAccount,
  setJwtToken,
  setRole,
  setUserDetail,
  setUserId,
  setOrganization,
} from 'src/redux/slice/accountSlice';
import {userServiceDetail} from 'src/apis/autoGenerate/services';
import {getLicenseFilteredMenus} from './menu';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';
import {AppSidebar} from 'src/components/AppSidebar/AppSidebar';
import {DrawerContext} from './DrawerContext';
import LicenseInsufficientBanner from 'src/components/LicenseInsufficientBanner';

const drawerWidth = 220;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {shouldForwardProp: prop => prop !== 'open'})(({theme, open}) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': {
      ...openedMixin(theme),
      borderRight: `1px solid ${theme.palette.divider}`,
      boxShadow: 'none',
      height: '100%',
      minHeight: '100vh',
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': {
      ...closedMixin(theme),
      borderRight: `1px solid ${theme.palette.divider}`,
      boxShadow: 'none',
      height: '100%',
      minHeight: '100vh',
    },
  }),
}));

export default function Layout({children}: {children?: React.ReactNode}) {
  const [open, setOpen] = useState(true);
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectAccount);
  const {isAtLimit} = useLicenseQuota();

  const handleDrawerOpenToggle = () => {
    setOpen(!open);
  };

  useEffect(() => {
    const userId = LocalDB.get(LocalDB.alias.userId);
    const jwtToken = LocalDB.get(LocalDB.alias.token);
    const role = LocalDB.get(LocalDB.alias.role);
    if (jwtToken && !account.jwtToken) {
      dispatch(setJwtToken(jwtToken));
    }
    if (userId && !account.userId) {
      dispatch(setUserId(userId));
      userServiceDetail(userId).then(res => {
        dispatch(setUserDetail(res.data));
      });
    }
    if (role && !account.role) {
      dispatch(setRole(JSON.parse(role)));
    }
    const organization = LocalDB.get(LocalDB.alias.organization);
    if (organization && !account.organization) {
      dispatch(setOrganization(JSON.parse(organization)));
    }
  }, [account, dispatch]);

  const filteredMenus = getLicenseFilteredMenus(account.role?.name, isAtLimit);

  return (
    <DrawerContext.Provider value={open}>
      <Box sx={{display: 'flex', minHeight: '100vh', bgcolor: 'background.default'}}>
        <CssBaseline />
        <Drawer variant="permanent" open={open}>
          <AppSidebar
            open={open}
            onToggle={handleDrawerOpenToggle}
            filteredMenus={filteredMenus}
            DrawerContext={DrawerContext}
          />
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: {xs: 2, sm: 3, md: 5},
            minWidth: 0,
          }}
        >
          <LicenseInsufficientBanner />
          {children}
        </Box>
      </Box>
    </DrawerContext.Provider>
  );
}
